import { Autocomplete, Card, MenuItem, TextField } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API } from '~/api';
import { UserCountrySource } from '~/shared/dto/model.enums';
import { Col, Row } from '../view.box';

export type DashboardCountriesChartProps = {
	source: UserCountrySource;
	onSourceChange?: (source: UserCountrySource) => void;
	countries: API.AnalyticsCountry[];
	height?: number;
};
export const DashboardCountriesChart: FC<DashboardCountriesChartProps> = observer(
	({ countries, source, onSourceChange, height = 600 }) => {
		const { t } = useTranslation('dto');
		const [filters, setFilters] = useState<API.AnalyticsCountry[]>([]);
		const handleChangeFilters = useCallback((_: unknown, value: API.AnalyticsCountry[]) => {
			setFilters(value);
		}, []);

		const handleChangeSource = useCallback(
			(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				const newSource = e.target.value as UserCountrySource;
				onSourceChange?.(newSource);
			},
			[onSourceChange],
		);

		const dataset = useMemo(() => {
			return countries
				.sort((a, b) => b.count - a.count)
				.filter(country => !filters.some(filter => filter.code === country.code))
				.map(country => {
					const item: API.AnalyticsCountry & Record<string, any> = { ...country };
					return item;
				});
		}, [countries, filters]);

		const { totalCountries, totalUsers } = useMemo(
			() => ({
				totalCountries: dataset.length,
				totalUsers: dataset.reduce((sum, country) => sum + country.count, 0),
			}),
			[dataset],
		);

		return (
			<Card elevation={2} sx={{ flex: 1 }}>
				<Col p={2}>
					<BarChart
						height={height}
						dataset={dataset}
						layout="horizontal"
						series={[{ dataKey: 'count', label: `${totalUsers} users from ${totalCountries} countries` }]}
						yAxis={[{ scaleType: 'band', dataKey: 'name', tickLabelStyle: { display: 'none' } }]}
					/>
					<Row gap={2} flex={1} justifyContent="space-between">
						<TextField sx={{ flex: 1 }} select label="Source" value={source} onChange={handleChangeSource}>
							{Object.values(UserCountrySource).map(value => (
								<MenuItem key={value} value={value}>
									{t(`UserCountrySource.${value}`)}
								</MenuItem>
							))}
						</TextField>

						<Autocomplete
							sx={{ flex: 3 }}
							multiple
							options={countries}
							renderInput={props => <TextField {...props} label="Exclude countries from chart" />}
							getOptionLabel={option => option.name}
							isOptionEqualToValue={(option, value) => option.code === value.code}
							value={filters}
							onChange={handleChangeFilters}
						/>
					</Row>
				</Col>
			</Card>
		);
	},
);
